/* eslint-disable one-var */
import newIcon from '../../00-utilities/icon';
import DropdownBase from './DropdownBase';

/**
 * @fileOverview Dropdown for select elements
 * @author Selectra Front End Team
 * @version 1.0.0
 * @requires /src/js/01-atoms/DropdownBase.js
 * @requires Polyfill:NodeList.forEach
 * @requires Polyfill:Element.classList
 */


/**
 * Represents a selection dropdown
 * @extends DropdownBase
 * @exports
 */
export default class DropdownList extends DropdownBase {
  /**
   * Create a selection dropdown
   * @param { object } element - A reference to the nav or select object
   * @param { object } options - A configuration object
   */
  constructor( baseElement, options, index, manager ) {
    super( baseElement, options, index, manager );

    this.select = {
      el: this.baseEl,
      selected: null,
    }

    this.type = 'select';
    this.list.isRebuilding = false;

    this._build();
    this._buildCommon();
    this._setCommonEventListeners();
    this._setEventListeners();

  }


  /**
   * Set-up DropdownSelect
   * @private
   */
  _build() {

    /**
     * Generate the text and icon of an item based in the option element data
     * @param { object } option -- Option element reference
     * @param { object } item   -- Block element reference (div, li)
     */
    const populateItem = ( option, item ) => {

      const
        iconIdLeft = option.dataset[ 'imageLeft' ],
        iconIdRight = option.dataset[ 'imageRight' ],
        itemText = document.createElement( 'span' );

      itemText.textContent = option.text;

      if ( iconIdLeft ) {
        const itemIcon =
          newIcon( iconIdLeft, [ 'icon--left' ], this.opt.spritePath );
        item.appendChild( itemIcon );
        item.appendChild( itemText );
      } else if ( iconIdRight ) {
        const itemIcon =
          newIcon( iconIdRight, [ 'icon--right' ], this.opt.spritePath );
        item.appendChild( itemText );
        item.appendChild( itemIcon );
      } else {
        item.textContent = option.text;
      }
    }

    if ( !this.list.isRebuilding ) {

      // -- Create elements
      const wrapper = document.createElement( 'div' );
      wrapper.classList.add( 'dropdown', 'dropdown--select' );

      this.select.el.classList.add( 'dropdown__select' );
      this.select.el.classList.remove( 'dropdown' );

      if ( this.select.el.classList.contains( 'dropdown--block' )) {
        wrapper.classList.add( 'dropdown--block' );
        this.select.el.classList.remove( 'dropdown--block' );
      }

      if ( this.select.el.classList.contains( 'dropdown--no-scroll' )) {
        wrapper.classList.add( 'dropdown--no-scroll' );
        this.select.el.classList.remove( 'dropdown--no-scroll' );
      }

      // -- Create trigger
      const trigger = document.createElement( 'button' ),
        option = this.select.el.querySelector( 'option:checked' ) ?
          this.select.el.querySelector( 'option:checked' ) :
          this.select.el.querySelector( 'option' );

      trigger.classList.add( 'dropdown__trigger' );
      populateItem( option, trigger );
      wrapper.appendChild( trigger );

      // -- Wrap select, insert trigger element
      this.baseEl.parentNode.replaceChild( wrapper, this.select.el );
      wrapper.insertBefore( this.select.el, trigger );

      this.baseEl = wrapper;
      this.trigger = trigger;

    }

    // Set disabled class
    this.list.isActive = !this.select.el.hasAttribute( 'disabled' )
    if ( !this.list.isActive ) {
      this.baseEl.classList.add( 'dropdown--disabled' );
    }

    // -- Create list
    const newList = document.createElement( 'ul' );
    newList.classList.add( 'dropdown__content' );

    Array.from( this.select.el.children ).forEach( option => {
      const listItem = document.createElement( 'li' );
      newList.appendChild( listItem );

      if ( option.hasAttribute( 'disabled' )) {
        listItem.setAttribute( 'disabled', '' );
        listItem.classList.add( 'dropdown__item--disabled' );
      }

      listItem.classList.add( 'dropdown__item', 'dropdown__item--option' );
      listItem.dataset[ 'value' ] = option.getAttribute( 'value' );

      populateItem( option, listItem );
    });

    this.list.html.appendChild( newList );
    this.list.el = newList;
    this.select.selected = this.select.el.firstElementChild;

  }


  /**
   * Rebuild the list
   */
  rebuild() {

    this.list.el.parentNode.removeChild( this.list.el );
    this.list.html = document.createDocumentFragment();
    this.list.isRebuilding = true;
    this._build();
    this._buildCommon();
    this.list.isRebuilding = false;

  }


  /**
   *  Set the currently selected option in the select element.
   */
  _activateSelected() {

    // Update hidden select element
    if ( this.select.selected ) {
      this.select.selected.removeAttribute( 'selected' );
    }

    const
      selector = `option[ value="${ this.list.selected.dataset.value }" ]`,
      triggerContent = document.createDocumentFragment();
    this.select.selected = this.select.el.querySelector( selector );
    this.select.selected.setAttribute( 'selected', '' );

    // Let the others know a select was updated
    this.select.el.dispatchEvent( new Event( 'change' ));

    // -- Set trigger content

    this.list.selected.childNodes.forEach( element => {
      triggerContent.appendChild( element.cloneNode( true ));
    });

    while ( this.trigger.firstChild ) {
      this.trigger.removeChild( this.trigger.firstChild );
    }

    this.trigger.appendChild( triggerContent );

    this.hideList();
    this.manager.activeDropdown = null;

  }


  /**
   * Set select specific eventListeners
   */
  _setEventListeners() {

    this.baseEl.addEventListener( 'mousedown', e => {

      e.preventDefault();

      if ( this.list.selected ) {
        this.list.selected.removeAttribute( 'aria-selected' );
      }

      this.list.selected = e.target;

      if ( this.list.selected.nodeName !== 'UL' ) {

        // Make sure we get a list element
        while ( this.list.selected.nodeName !== 'LI' ) {
          this.list.selected = this.list.selected.parentElement;
        }

        if ( this.list.selected.hasAttribute( 'disabled' )) {
          return;
        }

        this.list.selected.setAttribute( 'aria-selected', 'true' );

        this._activateSelected();

      }

    });
  }

}
